import React from "react";
import Image from "next/image";
import clock from "../../assets/icons/clock.svg";
import cross from "../../assets/icons/cross.svg";
import tick from "../../assets/icons/tick.svg";
import { motion } from "framer-motion";

interface StatsCardProps {
  score: string;
  nextReview: string;
  totalTime: string;
  question: string;
}

const StatsCard = ({
  score,
  nextReview,
  totalTime,
  question,
}: StatsCardProps) => {
  return (
    <div className="relative w-[328px] h-[274px] bg-white rounded-[28px] overflow-hidden border border-dashed border-black">
      {/* Stats Header */}
      <div className="flex w-[290px] items-center justify-between absolute top-[22px] left-[19px]">
        {/* Score */}
        <div className="flex flex-col w-[46px] items-center justify-center">
          <div className="font-semibold text-gray-600 text-xs">Score</div>
          <div className="font-extrabold text-black text-lg">{score}</div>
        </div>

        {/* Next Review */}
        <div className="flex flex-col items-center justify-center">
          <div className="font-semibold text-gray-600 text-xs">Next Review</div>
          <div className="font-extrabold text-black text-lg">{nextReview}</div>
        </div>

        {/* Total Time */}
        <div className="flex flex-col items-center justify-center">
          <div className="font-semibold text-gray-600 text-xs">Total Time</div>
          <div className="font-extrabold text-black text-lg">{totalTime}</div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="absolute w-full h-px top-[89px] bg-black opacity-20" />

      {/* Question Section */}
      <div className="flex flex-col w-[233px] items-start gap-1.5 absolute top-[105px] left-[19px]">
        <div className="flex flex-col items-start w-full">
          <div className="font-medium text-gray-500 text-[8px]">Question</div>
          <p className="font-medium text-black text-xs">{question}</p>
        </div>

        {/* Options */}
        <div className="flex flex-col w-[155px] items-start gap-1 px-3 py-0 relative">
          {/* Option A */}
          <div className="inline-flex items-center gap-2">
            <div className="font-semibold text-red-600 text-xs">A.</div>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "62px" }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 12,
                delay: 0.1,
              }}
              className="h-2 bg-red-500 rounded-[20px]"
            />
          </div>

          {/* Option B */}
          <div className="inline-flex items-center gap-2">
            <div className="font-medium text-black text-xs">B.</div>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "100px" }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 12,
                delay: 0.2,
              }}
              className="h-2 bg-gray-200 rounded-[20px]"
            />
          </div>

          {/* Option C */}
          <div className="inline-flex items-center gap-2">
            <div className="font-semibold text-green-600 text-xs">C.</div>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "121px" }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 12,
                delay: 0.3,
              }}
              className="h-2 bg-green-400 rounded-[20px]"
            />
          </div>

          {/* Option D */}
          <div className="inline-flex items-center gap-2">
            <div className="font-medium text-black text-xs">D.</div>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "140px" }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 12,
                delay: 0.4,
              }}
              className="h-2 bg-gray-200 rounded-[20px]"
            />
          </div>

          {/* Cross Icon for incorrect answer */}
          <Image
            className="absolute w-1.5 h-1.5 top-1.5 left-px"
            src={cross}
            alt="Cross"
            width={6}
            height={6}
          />

          {/* Tick Icon for correct answer */}
          <Image
            className="absolute w-[7px] h-[5px] top-[50px] left-px"
            src={tick}
            alt="Tick"
            width={7}
            height={5}
          />
        </div>
      </div>

      {/* Clock Icon */}
      <div className="absolute w-[23px] h-[22px] top-60 left-[286px]">
        <Image width={23} height={22} src={clock} alt="Clock" />
      </div>
    </div>
  );
};

export default StatsCard;
